import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext.jsx';
import { useClients } from '../../context/ClientsContext.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { apiFetch, authHeaders } from '../../lib/api.js';
import { ROUTES, hasMinRole, isRouteFeatureEnabled } from '../../routes.js';

// ─── helpers ─────────────────────────────────────────────────────────────────

function score(text, query) {
  const t = (text || '').toLowerCase();
  const q = query.toLowerCase();
  if (t === q) return 3;
  if (t.startsWith(q)) return 2;
  if (t.includes(q)) return 1;
  return 0;
}

function buildPageItems(role) {
  return ROUTES.filter(
    (r) => r.navVisible !== false && r.icon && hasMinRole(role, r.minRole) && isRouteFeatureEnabled(r)
  ).map((r) => ({ type: 'page', id: r.id, label: r.label, path: r.path }));
}

// ─── result item ──────────────────────────────────────────────────────────────

function ResultItem({ item, isActive, onClick, onMouseEnter }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isActive && ref.current) ref.current.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  const label = item.type === 'asin' ? item.asin : item.label;
  const sublabel = item.type === 'asin'
    ? [item.title, item.client_id].filter(Boolean).join(' · ')
    : item.type === 'client' ? (item.status ?? null) : null;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={[
        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
        isActive ? 'bg-n-50' : 'hover:bg-n-25',
      ].join(' ')}
    >
      <ResultIcon item={item} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-n-800 truncate">{label}</span>
        {sublabel && (
          <span className="block text-xs text-n-400 truncate mt-0.5">{sublabel}</span>
        )}
      </span>
      {item.type === 'page' && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-n-300 shrink-0" aria-hidden="true">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}

const STATUS_COLOURS = { green: 'bg-emerald-500', amber: 'bg-amber-400', red: 'bg-red-500' };

function ResultIcon({ item }) {
  if (item.type === 'client') {
    const dot = STATUS_COLOURS[item.sentiment] ?? 'bg-n-200';
    return (
      <span className="w-6 h-6 rounded-full bg-sitruna-yellow/15 grid place-items-center shrink-0 relative">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-sitruna-yellow" aria-hidden="true">
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3.5 19c.7-3 3-4.7 5.5-4.7s4.8 1.7 5.5 4.7" />
        </svg>
        {item.sentiment && (
          <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${dot}`} />
        )}
      </span>
    );
  }
  if (item.type === 'asin') {
    return (
      <span className="w-6 h-6 rounded bg-n-50 border border-n-100 grid place-items-center shrink-0">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-n-400" aria-hidden="true">
          <rect x="3.5" y="4" width="17" height="5" rx="1" />
          <rect x="3.5" y="10.5" width="17" height="3.5" rx="1" />
          <rect x="3.5" y="15.5" width="17" height="4" rx="1" />
        </svg>
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded bg-n-50 border border-n-100 grid place-items-center shrink-0">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-n-500" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="M20 20l-4-4" />
      </svg>
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-4 pt-3 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-n-400 select-none">
      {children}
    </div>
  );
}

// ─── main modal ───────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

export default function UnifiedSearch() {
  const { isOpen, close } = useSearch();
  const { clients } = useClients();
  const { role, getToken } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [asinResults, setAsinResults] = useState([]);
  const [asinLoading, setAsinLoading] = useState(false);
  const [indexWarmed, setIndexWarmed] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setAsinResults([]);
      setActiveIdx(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isOpen]);

  // Global ASIN search — debounced, fires for any query ≥ 2 chars
  useEffect(() => {
    if (!isOpen || query.trim().length < MIN_QUERY_LEN) {
      setAsinResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setAsinLoading(true);
      try {
        const token = await getToken();
        const { data, meta } = await apiFetch(
          `/api/search?q=${encodeURIComponent(query.trim())}`,
          { headers: authHeaders(token) }
        );
        setIndexWarmed(meta?.index_warmed !== false);
        setAsinResults((data || []).map((a) => ({
          type: 'asin',
          id: `${a.asin}:${a.client_id}:${a.marketplace}`,
          asin: a.asin,
          title: a.title,
          client_id: a.client_id,
          marketplace: a.marketplace,
        })));
      } catch {
        setAsinResults([]);
      } finally {
        setAsinLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, isOpen, getToken]);

  const q = query.trim().toLowerCase();

  const pageItems = buildPageItems(role)
    .filter((p) => !q || score(p.label, q) > 0)
    .sort((a, b) => score(b.label, q) - score(a.label, q))
    .slice(0, 6);

  const clientItems = clients
    .filter((c) => !q || score(c.brand_name, q) > 0 || score(c.client_id, q) > 0)
    .sort((a, b) => {
      const sa = Math.max(score(a.brand_name, q), score(a.client_id, q));
      const sb = Math.max(score(b.brand_name, q), score(b.client_id, q));
      return sb - sa;
    })
    .slice(0, 6)
    .map((c) => ({
      type: 'client',
      id: c.client_id,
      label: c.brand_name,
      status: c.overall_status,
      sentiment: c.sentiment,
      path: `/clients/${c.client_id}`,
    }));

  const asinSection = q.length >= MIN_QUERY_LEN
    ? [{ key: 'asins', label: 'ASINs', items: asinResults, loading: asinLoading }]
    : [];

  const groups = [
    { key: 'pages', label: 'Pages', items: pageItems },
    { key: 'clients', label: 'Clients', items: clientItems },
    ...asinSection,
  ].filter((g) => g.items.length > 0 || g.loading);

  const flatItems = groups.flatMap((g) => g.items);
  const clampedIdx = Math.min(activeIdx, Math.max(0, flatItems.length - 1));

  const handleNavigate = useCallback((item) => {
    close();
    if (item.type === 'asin') {
      navigate(`/catalogue/${item.asin}?client=${item.client_id}&marketplace=${item.marketplace}`);
    } else {
      navigate(item.path);
    }
  }, [close, navigate]);

  function handleKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && flatItems[clampedIdx]) { e.preventDefault(); handleNavigate(flatItems[clampedIdx]); }
  }

  if (!isOpen) return null;

  let flatIdx = 0;
  const renderedGroups = groups.map((g) => {
    const rendered = g.items.map((item) => {
      const idx = flatIdx++;
      return (
        <ResultItem
          key={item.id}
          item={item}
          isActive={idx === clampedIdx}
          onClick={() => handleNavigate(item)}
          onMouseEnter={() => setActiveIdx(idx)}
        />
      );
    });
    return (
      <div key={g.key}>
        <SectionLabel>{g.label}{g.loading ? ' …' : ''}</SectionLabel>
        {rendered}
      </div>
    );
  });

  const isEmpty = !q && flatItems.length === 0;
  const noResults = q.length >= MIN_QUERY_LEN && flatItems.length === 0 && !asinLoading;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" aria-hidden="true" onClick={close} />

      <div
        role="dialog"
        aria-label="Search"
        aria-modal="true"
        className="fixed left-1/2 top-[12vh] z-50 w-full max-w-xl -translate-x-1/2 rounded-xl bg-white shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '70vh' }}
        onKeyDown={handleKeyDown}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 border-b border-n-100 shrink-0">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-n-400 shrink-0" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="M20 20l-4-4" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search clients, pages, ASINs…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            className="flex-1 h-12 text-[14px] text-n-800 placeholder:text-n-300 bg-transparent outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="text-[11px] text-n-300 font-sans border border-n-100 rounded px-1.5 py-0.5 shrink-0">ESC</kbd>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1">
          {isEmpty && (
            <p className="px-4 py-6 text-sm text-n-400 text-center">
              Start typing to search clients, pages, and ASINs.
            </p>
          )}
          {noResults && (
            <p className="px-4 py-6 text-sm text-n-400 text-center">
              No results for <span className="font-medium text-n-600">"{query}"</span>
            </p>
          )}
          {!indexWarmed && q.length >= MIN_QUERY_LEN && !asinLoading && asinResults.length === 0 && (
            <p className="px-4 pb-3 text-xs text-n-300 text-center">
              ASIN index not yet warmed — run cache refresh to enable global ASIN search.
            </p>
          )}
          {renderedGroups}
        </div>

        {/* Footer */}
        {flatItems.length > 0 && (
          <div className="border-t border-n-100 px-4 py-2 flex items-center gap-4 text-[11px] text-n-300 shrink-0">
            <span><kbd className="font-sans">↑↓</kbd> navigate</span>
            <span><kbd className="font-sans">↵</kbd> open</span>
            <span><kbd className="font-sans">ESC</kbd> close</span>
          </div>
        )}
      </div>
    </>
  );
}
